// Login.jsx — schermata di login
// Dipendenze: LoreLogo, axios (o fetch)
// Usato in App.jsx come guard prima delle route protette

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoreLogo from '../components/LoreLogo';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const EXPIRY_KEY = 'lore_auth_expiry';
const USER_KEY = 'lore_auth_user';

export function getStoredToken() {
  // Cleanup legacy token from localStorage (pre-cookie migration)
  localStorage.removeItem('lore_auth_token');
  const expiry = parseInt(localStorage.getItem(EXPIRY_KEY) || '0');
  if (!expiry || Date.now() > expiry) {
    localStorage.removeItem(EXPIRY_KEY);
    localStorage.removeItem(USER_KEY);
    return null;
  }
  // Ritorna truthy generico — il token vero è nel cookie httpOnly
  return 'cookie';
}

export function saveToken(token, expiresAt, user) {
  // token non viene più salvato in localStorage — è nel cookie httpOnly
  localStorage.setItem(EXPIRY_KEY, String(expiresAt));
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch { return null; }
}

export function clearToken() {
  localStorage.removeItem('lore_auth_token'); // legacy cleanup (pre-cookie migration)
  localStorage.removeItem(EXPIRY_KEY);
  localStorage.removeItem(USER_KEY);
}

export default function Login({ onLogin }) {
  const { t } = useTranslation(['auth', 'errors', 'nav']);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username || !password) { setError(t('required', { ns: 'auth' })); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (res.ok && data.expiresAt) {
        saveToken(null, data.expiresAt, data.user);
        onLogin('cookie', data.user);
      } else {
        setError(data.error ? t(data.error, { ns: 'errors', defaultValue: data.error }) : t('INVALID_CREDENTIALS', { ns: 'errors' }));
        setPassword('');
      }
    } catch {
      setError(t('serverUnreachable', { ns: 'auth' }));
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-0)',
    }}>
      <div style={{
        width: 'min(380px, 92vw)', background: 'var(--bg-2)', border: '1px solid var(--border)',
        borderRadius: 16, padding: 'clamp(20px, 5vw, 40px) clamp(16px, 5vw, 32px)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
        animation: 'fadeIn 0.3s ease',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 32 }}>
          <LoreLogo size={100} />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            {t('tagline', { ns: 'nav' })}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="lore-username" style={{ display: 'block', fontSize: 10, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6 }}>
              {t('username', { ns: 'auth' })}
            </label>
            <input
              id="lore-username"
              type="text" autoComplete="username"
              value={username} onChange={e => setUsername(e.target.value)}
              style={{
                width: '100%', background: 'var(--bg-3)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '10px 14px', color: 'var(--text-1)',
                fontFamily: 'var(--font-mono)', fontSize: 13, outline: 'none',
              }}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="lore-password" style={{ display: 'block', fontSize: 10, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6 }}>
              {t('password', { ns: 'auth' })}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="lore-password"
                type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                value={password} onChange={e => setPassword(e.target.value)}
                style={{
                  width: '100%', background: 'var(--bg-3)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '10px 14px', paddingRight: 40, color: 'var(--text-1)',
                  fontFamily: 'var(--font-mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                aria-label="toggle show password"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)',
                  padding: 2, fontSize: 13, lineHeight: 1,
                }}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ fontSize: 11, color: 'var(--red)', textAlign: 'center', marginBottom: 12 }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              width: '100%', padding: 12, background: '#FF9C00', color: '#000',
              border: 'none', borderRadius: 8, fontFamily: 'var(--font-mono)',
              fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? t('loggingIn', { ns: 'auth' }) : t('submit', { ns: 'auth' })}
          </button>
        </form>

        {/* Synclab footer */}
        <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)', textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            {t('poweredBy', { ns: 'auth' })}
          </div>
          {/* Synclab logo inline SVG ridotto */}
          <svg width="80" height="24" viewBox="0 0 201 60" fill="none">
            <path d="M63.887 15.717C63.887 15.717 86.1205 0 118.464 0C150.195 0 143.376 15.717 143.376 15.717L126.611 15.6856C126.611 15.6856 134.435 6.1017 113.742 6.1017C85.4034 6.1017 63.9244 15.717 63.9244 15.717H63.887Z" fill="#FF9C00"/>
            <path d="M141.527 44.283C141.527 44.283 119.294 60 86.9498 60C55.2222 60 62.0382 44.283 62.0382 44.283L78.8034 44.3144C78.8034 44.3144 70.9794 53.8983 91.6724 53.8983C120.011 53.8983 141.493 44.283 141.493 44.283H141.527Z" fill="#4A5568"/>
            <path d="M13.2981 29.6089C12.6271 29.366 11.9216 29.1689 11.1959 29.0175C10.4674 28.8661 9.69278 28.7719 8.89801 28.7319C8.11187 28.6862 7.3603 28.6405 6.64039 28.5862C5.92337 28.5348 5.22938 28.4748 4.57859 28.4091C3.05527 28.252 2.87961 27.4721 2.87961 26.8979C2.87961 26.6209 2.95448 26.3923 3.10998 26.2009C3.277 25.9924 3.50161 25.821 3.77517 25.6867C4.0689 25.5439 4.40293 25.4354 4.76576 25.3582C5.14299 25.2783 5.52598 25.2183 5.90033 25.1783C6.27756 25.1383 6.64327 25.1154 6.98307 25.1069C7.33438 25.0983 7.61946 25.0954 7.84407 25.0954C8.83754 25.0954 9.79933 25.2011 10.7035 25.4125C11.6048 25.621 12.4486 25.9324 13.2088 26.3352L15.6737 24.8069C14.2541 23.8614 13.0648 23.3215 11.7834 22.9701C10.5077 22.6188 9.18309 22.4417 7.84407 22.4417C7.40637 22.4417 6.91396 22.4616 6.37835 22.4988C5.84274 22.5359 5.28697 22.6102 4.72833 22.7216C4.1668 22.833 3.6168 22.9873 3.09271 23.1815C2.55422 23.3815 2.06756 23.6471 1.65002 23.9728C1.22384 24.3042 0.875403 24.7126 0.613358 25.1868C0.348433 25.6696 0.213092 26.2466 0.213092 26.9037C0.213092 28.7176 0.935875 29.8974 2.3584 30.4087C3.69167 30.9 5.17755 31.1914 6.77285 31.2743L11.0779 31.6085C12.6012 31.7656 12.7769 32.5397 12.7769 33.111C12.7769 33.3967 12.6991 33.6252 12.5436 33.8166C12.3708 34.0252 12.1462 34.1994 11.8698 34.3308C11.5761 34.4736 11.242 34.5851 10.8763 34.665C10.4991 34.7479 10.119 34.8107 9.74462 34.8507C9.37027 34.8907 9.01031 34.9136 8.67628 34.9164C8.3336 34.9193 8.05716 34.9221 7.84695 34.9221C6.87652 34.9221 5.91473 34.8164 4.99037 34.6108C4.07178 34.4051 3.22229 34.0909 2.46207 33.6738L0 35.2021C0.967553 35.8705 2.10787 36.399 3.39218 36.7703C4.67073 37.1417 6.05871 37.3274 7.51003 37.3274C8.27601 37.5416 8.76267 37.5074 9.2954 37.5074C9.83676 37.4731 10.3897 37.3988 10.9425 37.2902C11.4983 37.1788 12.0454 37.0246 12.5638 36.8303C13.0936 36.6304 13.5774 36.3647 13.9949 36.0419C14.4211 35.7106 14.7724 35.3021 15.0373 34.8279C15.308 34.3451 15.4491 33.7652 15.4549 33.0996C15.4549 31.3114 14.7292 30.1373 13.2981 29.6089Z" fill="#4A5568"/>
          </svg>
        </div>
      </div>
    </div>
  );
}
